<template>
  <div id="reflection-page">
    <ReflectionHeader
      v-if="isEditorReady"
      :editor="getEditor()"
      :reflection="reflection"
      :editor-status="editorStatus"
      :editor-focused-block="editorFocusedBlock"
      :users="users"
    />
    <div class="freeRedactionContainer">
      <div class="side-section-container">
        <div class="leftContainer">
          <h1 class="section-title">
            {{ $t('views.reflection.tableOfContent') }}
          </h1>
          <perfect-scrollbar style="height: calc(50vh - 81px)">
            <TableOfContent
              :sections="sections"
              @sectionSelected="sectionSelected"
              class="leftItem"
            />
          </perfect-scrollbar>
          <h1 class="section-title mt-2">
            {{ $t('views.reflection.bibliography') }}
          </h1>
          <perfect-scrollbar style="height: calc(50vh - 89px)">
            <ReflectionAnalyses
              v-if="isEditorReady"
              :analyses="currentReflectionAnalyses"
              :reflection-id="reflectionId"
              @insert-block="insertBlock"
              @insert-supporting-doc="insertSupportingDoc"
            />
          </perfect-scrollbar>
        </div>
      </div>
      <EditorPage
        :reflection="reflection"
        height="calc(100vh - 96px)"
        @editorReady="editorReady"
        @editorChange="editorChange"
        @editorStatusChange="editorStatusChange"
        @editorScroll="onEditorScroll"
        class="noos-editor"
      >
        <template v-slot:tools>
          <SideComments v-if="isEditorReady" :editor="getEditor()" ref="sideComments" />
        </template>
      </EditorPage>
      <div class="side-section-container" />
    </div>
  </div>
</template>
<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import get from 'lodash/get';
import { Debouncer } from '@/shared/services/debouncer';
import * as analysisConstants from '@/store/analysisConstants';
import EditorPage from './tiptap/EditorPage';
import { CHANGE_TYPES } from './tiptap/changes';
import ReflectionHeader from '../ReflectionHeader';
import TableOfContent from './editorjs/TableOfContent';
import ReflectionAnalyses from './ReflectionAnalyses';
import { types } from '../../../../store/rootTypes';
import SideComments from './SideComment/SideComments';

export default {
  name: 'ReflectionFreeRedaction',
  components: {
    TableOfContent,
    ReflectionAnalyses,
    ReflectionHeader,
    EditorPage,
    SideComments,
  },
  props: {
    reflectionId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      updateEditorState: undefined,
      updateEditorChangeType: undefined,
      isEditorReady: false,
      editorStatus: 'n/a',
      sections: [],
      currentEditorBlock: -1,
      currentTab: '',
      comments: [],
      recommendations: [],
      editorFocusedBlock: {},
      showCommentButton: false,
      users: [],
    };
  },
  computed: {
    ...mapGetters({
      displayName: types.authentication.getters.displayName,
      userId: types.authentication.getters.userId,
      reflection: types.reflections.getters.reflection,
      reflectionReadOnly: types.reflections.getters.reflectionReadOnly,
      usersProfiles: types.users.getters.usersProfiles,
      currentReflectionAnalyses: types.reflections.getters.currentReflectionAnalyses,
    }),
    collaborationCursorUsers() {
      return this.isEditorReady ? this.getEditor().storage.collaborationCursor.users : [];
    },
    bibliographySources() {
      return this.currentReflectionAnalyses
        .filter(reflection => !!reflection.analysisData)
        .map(reflection => ({
          title: reflection.analysisData.documentDetails.title,
          url: reflection.analysisData.documentDetails.url,
        }));
    },
  },
  methods: {
    ...mapActions({
      updateCurrentReflectionData: types.reflections.actions.updateCurrentReflectionData,
    }),
    getEditor() {
      return window.nooseditor;
    },
    focusOnClick() {
      const editor = this.getEditor();
      if (!editor.view.focused) {
        editor.chain().focus().run();
      }
    },
    editorReady(data) {
      console.log('editorReady ReflectionFreeRedaction', data);
      this.editorContentIndex = data.contentIndex;
      // TODO delete the following line when vue editor will be RIP
      this.getEditor().storage.supportingDocuments.references = [...this.currentReflectionAnalyses];
      this.updateEditorState = new Debouncer(() => this.updateEditorStateCallback(), 1000, true);
      this.isEditorReady = true;
      this.getEditor().chain().focus().run();
      this.tocUpdate();
    },
    editorStatusChange(newStatus) {
      this.editorStatus = newStatus;
    },
    editorChange(changeType) {
      if (!this.isEditorReady) {
        console.warn('editor not ready.');
        return;
      }

      this.updateEditorChangeType = changeType;
      this.updateEditorState.execute();
    },
    updateEditorStateCallback() {
      if (this.updateEditorChangeType === CHANGE_TYPES.tiptapChange) {
        this.processTiptapChange();
      }
      this.processBasicChange();
    },
    processTiptapChange() {
      const contentJson = this.getEditor().getJSON();
      this.updateCurrentReflectionData(contentJson);
    },
    processBasicChange() {
      const cursorPosition = this.getCursorPosition();

      if (cursorPosition) {
        this.editorFocusedBlock = this.editorContentIndex.getNodeAt(cursorPosition);
      }

      this.tocUpdate();
    },
    getCursorPosition() {
      const cursorPosition = get(this.getEditor().state, 'selection.$cursor.pos');
      if (!cursorPosition) {
        // Normally happen when the user is selecting text.
        // We set the cursor position to the anchor, or the
        // initial position the cursor was in at the beginning
        // of the selection.
        return get(this.getEditor().state, 'selection.$anchor.pos');
      }

      return cursorPosition;
    },
    updateComments() {
      const markers = this.$refs.editorContainer.$el.querySelectorAll('.noos-comment-marker');
      this.comments = Array.from(markers).map((currentMarker) => ({
        id: currentMarker.getAttribute('id'),
      }));
    },
    updateComment(block) {
      const blockText = block.blockData.data.text;
      const doc = new DOMParser().parseFromString(blockText, 'text/html');
      const commentsElements = doc.querySelectorAll('.noos-comment-marker');
      if (commentsElements.length !== 0) {
        const commentIDs = [];
        commentsElements.forEach(el => {
          commentIDs.push(el.getAttribute('id'));
        });
        const blockId = block.blockData.id;

        const existIndex = this.comments.findIndex(comment => comment.blockId === blockId);
        const commentData = { blockId, commentIDs, timestamp: Date.now() };

        // this.comments is an array with an entry for each block with its comments IDs
        if (existIndex === -1) {
          this.comments.push(commentData);
        } else {
          Vue.set(this.comments, existIndex, commentData);
        }
      }
    },
    onTabClick(tabId) {
      this.currentTab = tabId;
      this.$router.replace({
        ...this.$route,
        hash: `#${this.currentTab}`,
      });
    },
    onSelectionUpdate() {
      this.tocUpdate();
    },
    onEditorScroll(event) {
      this.$refs.sideComments.onEditorScroll(event);
    },
    insertSupportingDoc(analysisId) {
      if (this.reflectionReadOnly) return;

      this.getEditor().commands.insertSupportingDocument(analysisId);
    },
    insertBlock(blockData) {
      if (this.reflectionReadOnly) return;

      let originalHost = '';

      try {
        if (blockData.data.url) {
          originalHost = new URL(blockData.data.url).host;
        }
      } catch (e) {
        console.warn(`NoosAnalysisReflectionComment Could not extract host from url ${blockData.data.url}`, e);
      }

      const documentHost = blockData.data.isUpload
        ? this.$t('views.analysis.ficheInfoLinkName')
        : blockData.data.documentHost || originalHost;

      const originString = (() => {
        if (blockData.type === analysisConstants.ANALYSIS_REFLECTION_COMMENT) {
          return `${this.$t('views.analysis.commentBy')} ${blockData.data.username}`;
        }
        if (blockData.type === analysisConstants.ANALYSIS_SIGNIFICANT_ELEMENT) {
          return `${this.$t('views.analysis.highlightBy')} ${blockData.data.username}`;
        }
        if (blockData.type === analysisConstants.ANALYSIS_DOCUMENT) {
          return blockData.data.username
            ? `${this.$t('views.analysis.foundBy')} ${blockData.data.username}`
            : '';
        }
        return blockData.data.username;
      })();

      const text = blockData.type === analysisConstants.ANALYSIS_DOCUMENT
        ? blockData.data.title : blockData.data.text;

      const contentToInsert = [
        {
          type: 'blockquote',
          content: [
            {
              type: 'noosElementV2',
              attrs: {
                elementType: blockData.type,
                username: blockData.data.username,
              },
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      type: 'text',
                      marks: [
                        {
                          type: 'bold',
                        },
                      ],
                      text,
                    },
                  ],
                },
                {
                  type: 'noosElementFooter',
                  attrs: {
                    origin: originString,
                  },
                  content: [
                    ...(blockData.data.redirectUrl !== '' && documentHost !== '')
                      ? [{
                        type: 'text',
                        marks: [
                          {
                            type: 'link',
                            attrs: {
                              href: blockData.data.redirectUrl,
                              target: '_blank',
                            },
                          },
                          {
                            type: 'bold',
                          },
                        ],
                        text: documentHost,
                      }] : [],
                  ],
                },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
          content: [],
        },
      ];

      this.getEditor().commands.insertContent(contentToInsert);
      this.getEditor()
        .chain()
        .focus()
        .scrollIntoView()
        .run();
    },
    tocUpdate() {
      this.editorContentIndex.update();
      this.sections = this.editorContentIndex.getHeaderHierarchy(this.getCursorPosition());
    },
    sectionSelected(block) {
      this.getEditor().commands.focus(block.cursorPosition);
      this.getEditor().commands.scrollIntoView();
    },
    onConnectedUsersUpdate(users) {
      const usersMap = new Map();
      users.forEach(user => {
        usersMap.set(user.userId, user);
      });

      this.users = Array.from(usersMap.values());
    },
  },
  watch: {
    collaborationCursorUsers(users) {
      this.onConnectedUsersUpdate(users);
    },
    currentReflectionAnalyses(analyses) {
      if (this.isEditorReady) {
        this.getEditor().storage.supportingDocuments.references = [...analyses];
      }
    },
  },
};
</script>
<style scoped>
.section-title {
  font-weight: 400;
  font-size: 18px;
  line-height: 18px;
  margin-left: 8px;
  margin-right: 16px;
  padding-bottom: 4px;
  padding-left: 4px;
  border-bottom: 1px solid #ddd;
}
.freeRedactionContainer {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: stretch;
  overflow-y: hidden;
}

.editor {
  margin: 0;
  padding-top: 3rem;
}

.leftContainer {
  display: flex;
  flex-direction: column;
}

.leftItem {
  overflow-y: hidden;
  overflow-x: hidden;
  margin: 0px;
  padding: 0px;
}

.side-section-container {
  flex-basis: 100px;
  max-width: 800px;
  flex-grow: 1;
  margin-top: 20px;
}
.noos-editor {
  flex-basis: 842px;
  flex-grow: 0;
  flex-shrink: 0;
}
</style>
